import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { VendorService } from "src/app/services/vendor.service";
import Swal from "sweetalert2";
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { EditDeleteActionsRenderer } from '../../shared/ag-grid/cell-renderers/edit-delete.component';
import { ColumnApi, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AppCoreService } from 'src/app/services/app-core.service';
import { Title } from '@angular/platform-browser';
import { Utilities } from 'src/app/models/utilities';

@Component({
  selector: 'app-addvendorservices',
  templateUrl: './addvendorservices.component.html',
  styleUrls: ['./addvendorservices.component.scss']

})
export class AddVendorServicesComponent implements OnInit, AfterViewInit {
  vendorId: string;
  servicesForm: FormGroup;
  permConfig = {
    isViewAllowed: false,
    isAddAllowed: false,
    isEditAllowed: false,
    isDeleteAllowed: false
  };
  isFormSubmitted: boolean;

  editMode: boolean = false;
  serviceId: string;
  vendorServiceId: number;
  unitOptions: any[] = [{ name: 'KGS' }, { name: 'LBS' }, { name: 'CBM' }];

  private gridApi!: GridApi;
  private gridColumnApi!: ColumnApi;
  public context: any;
  defaultColDef = {
    sortable: true
  };

  columnDefs = [
    { headerName: 'Name', field: 'servicename' },
    { headerName: 'Min Weight', field: 'weightinfo.min' },
    { headerName: 'Max Weight', field: 'weightinfo.max' },
    { headerName: 'Charge Size Above', field: 'chargeinfo.abovesize' },
    { headerName: 'Charge Weight Above', field: 'chargeinfo.aboveweight' },
    {
      headerName: 'Actions',
      field: 'id',
      cellRenderer: EditDeleteActionsRenderer,
      colId: 'params',
      editable: false,
      minWidth: 150,
    },
  ];
  gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 20,
    domLayout: 'autoHeight'
  }
  rowData = [];
  useEnterKey: any;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private appCoreService: AppCoreService,
    private ngxLoaderService: NgxUiLoaderService,
    private vendorService: VendorService,
    private titleService: Title,
    private utilities: Utilities,
  ) {
    titleService.setTitle('Vendors');
    this.context = { componentParent: this, actionConfig: { config: true } };
  }

  ngOnInit() {
    this.servicesForm = this.formBuilder.group({
      txtVendor: new FormControl({ value: null, disabled: true }, Validators.compose([Validators.required])),
      txtService: [null, Validators.compose([Validators.required, Validators.maxLength(50)])],
      txtMinWeight: [null],
      txtMaxWeight: [null],
      ddlUnit: ['KGS'],
      txtChargeAboveSize: [null],
      txtChargeAboveWeight: [null],
      status: [true],
      chkProformaDocument: [false],
      chkProformaDetails: [false]
    });

    this.isFormSubmitted = false;
    this.vendorId = this.route.snapshot.params['id'];
    this.checkPermission();
    if (this.permConfig.isEditAllowed) {
      this.bindVendorDetailsById();
    }
  }

  ngAfterViewInit(): void {
    this.useEnterKey ? this.utilities.focusFirstElement(this.servicesForm) : null;
  }

  checkPermission(): any {
    const userData = this.appCoreService.GetUserData();
    this.useEnterKey = !userData.metaInfo.tabbasedtemplate;
    const permission = userData.config.filter(x => x.categoryname === 'Vendors');
    if (permission.length) {
      this.permConfig.isViewAllowed = permission[0].view;
      this.permConfig.isAddAllowed = permission[0].add;
      this.permConfig.isEditAllowed = permission[0].edit;
      this.permConfig.isDeleteAllowed = permission[0].delete;
    }
  }

  checkWeight(): boolean {
    const maxWeight = this.servicesForm.controls.txtMaxWeight.value;
    const minWeight = this.servicesForm.controls.txtMinWeight.value;
    if (maxWeight && minWeight && maxWeight < minWeight) {
      Swal.fire({
        titleText: 'Opps!',
        text: 'Min Weight cannot be Greater then Max Weight',
        icon: 'info',
        confirmButtonText: 'OK',
        allowEscapeKey: false,
        allowOutsideClick: false
      })
      return true;
    }
    return false;
  }

  bindVendorDetailsById() {
    try {
      this.ngxLoaderService.start();
      this.vendorService.GetById(this.vendorId).subscribe((res) => {
        if (res && res.success) {
          this.servicesForm.patchValue({
            txtVendor: res.data[0].name,
          });
          this.bindServiceList(true);
          this.ngxLoaderService.stop();
        }
      })
    } catch (e) {
      console.error(e);
      this.ngxLoaderService.stop();
    }
  }

  bindServiceList(refresh = false) {
    try {
      this.vendorService.GetAllListByVendor({ id: this.vendorId ,refresh: refresh}).subscribe((res) => {
        if (res && res.success) {
          this.rowData = res.data;
        }
      })
    } catch (e) {
      console.error(e);
    }
  }

  addServices() {
    if (!this.permConfig.isAddAllowed && !this.editMode) {
      this.warningDialog('No access to Add.');
      return;
    }
    else if (!this.permConfig.isEditAllowed && this.editMode) {
      this.warningDialog('No access to Edit.');
      return;
    }
    if (this.checkWeight()) {
      return;
    }
    try {
      this.isFormSubmitted = true;
      if (this.permConfig.isAddAllowed && this.servicesForm.valid || this.permConfig.isEditAllowed && this.servicesForm.valid) {
        if (this.editMode) {
          const requestObj = {
            id: this.serviceId,
            servicename: this.servicesForm.get('txtService').value,
            weightinfo: {
              min: this.servicesForm.get('txtMinWeight').value,
              max: this.servicesForm.get('txtMaxWeight').value,
              unit: this.servicesForm.get('ddlUnit').value,
            },
            chargeinfo: {
              description: '',
              abovesize: this.servicesForm.get('txtChargeAboveSize').value,
              aboveweight: this.servicesForm.get('txtChargeAboveWeight').value,
            },
            status: this.servicesForm.get('status').value,
            config: {
              proforma: {
                detailsmandatory: this.servicesForm.get('chkProformaDetails').value,
                documentrequired: this.servicesForm.get('chkProformaDocument').value,
              }
            }
          }
          this.ngxLoaderService.start();
          this.vendorService.UpdateService(requestObj).subscribe((res) => {
            try {
              if (res && res?.success) {
                this.ngxLoaderService.stop();
                this.openConfirmationDialog("Service updated successfully");
                this.resetForm();
                // this.bindServiceList();
              } else if (res && res?.success) {
                this.ngxLoaderService.stop();
              }
            }
            catch (err) {
              console.error(err);
            }
          });
        } else {
          const requestObj = {
            vendorid: this.vendorId,
            servicename: this.servicesForm.get('txtService').value,
            weightinfo: {
              min: this.servicesForm.get('txtMinWeight').value,
              max: this.servicesForm.get('txtMaxWeight').value,
              unit: this.servicesForm.get('ddlUnit').value,
            },
            chargeinfo: {
              description: '',
              abovesize: this.servicesForm.get('txtChargeAboveSize').value,
              aboveweight: this.servicesForm.get('txtChargeAboveWeight').value,
            },
            status: this.servicesForm.get('status').value,
            config: {
              proforma: {
                detailsmandatory: this.servicesForm.get('chkProformaDetails').value,
                documentrequired: this.servicesForm.get('chkProformaDocument').value,
              }
            }
          };
          // console.log(requestObj);
          this.vendorService.AddService(requestObj).subscribe((res) => {
            try {
              if (res && res.success) {
                this.openConfirmationDialog("Service added successfully");
                this.resetForm();
                // this.bindServiceList();
              }
            }
            catch (err) {
              console.error(err);
            }
          });
        }


      }
    } catch (e) {

    }
  }

  deleteService(id) {
    try {
      if (id) {
        this.vendorService.DeleteService(id).subscribe((res) => {
          if (res && res.success) {
            this.resetForm();
            this.bindServiceList(true);
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.setDomLayout("autoHeight");
    params.api.sizeColumnsToFit();

  }

  editAction(cell: any) {
    // console.log("C", cell);
    try {

      if (!this.permConfig.isEditAllowed) {
        this.warningDialog('No access to Edit.');
      }
      else if (this.permConfig.isEditAllowed && cell && cell.id) {
        this.editMode = true;
        this.serviceId = cell.id;
        this.servicesForm.patchValue({
          txtService: cell.servicename,
          txtMinWeight: cell.weightinfo.min,
          txtMaxWeight: cell.weightinfo.max,
          ddlUnit: cell.weightinfo.unit,
          txtChargeAboveSize: cell.chargeinfo.abovesize,
          txtChargeAboveWeight: cell.chargeinfo.aboveweight,
          status: cell.status,
          chkProformaDocument: cell.configs?.proforma?.documentrequired,
          chkProformaDetails: cell.configs?.proforma?.detailsmandatory
        });
      }

    } catch (e) {
      console.error(e);
    }

  }

  deleteAction(cell: any) {
    // console.log(cell);
    if (!this.permConfig.isDeleteAllowed) {
      this.warningDialog('No access to Delete.');
    }
    else if (this.permConfig.isDeleteAllowed && cell && cell.id) {
      this.openConfirmationDialog('Are you sure you want to delete ?', cell.id);
    }
  }

  resetForm(): void {
    this.servicesForm.reset({
      txtVendor: this.servicesForm.get('txtVendor').value,
      status: true
    });
    this.isFormSubmitted = false;
    this.editMode = false;
    this.serviceId = '';
  }

  async openConfirmationDialog(msg: any, id?: number) {
    let result;
    if (id) {
      result = await Swal.fire({
        titleText: 'Confirmation',
        text: msg,
        icon: 'warning',
        confirmButtonText: 'Yes',
        showDenyButton: true,
        denyButtonText: 'No',
        allowEscapeKey: false,
        allowOutsideClick: false
      });
      if (result.isConfirmed) {
        // call delete api
        this.deleteService(id);

      }
    } else {
      result = await Swal.fire({
        titleText: 'Success!',
        text: msg,
        icon: 'success',
        confirmButtonText: 'OK',
        allowEscapeKey: false,
        allowOutsideClick: false
      });
      if (result.isConfirmed) {
        this.bindServiceList(true);
      }
    }
  }

  warningDialog(msg: any) {
    Swal.fire({
      title: 'Warning!',
      text: msg,
      icon: 'warning',
      confirmButtonText: 'OK',
      allowEscapeKey: false,
      allowOutsideClick: false
    });
  }
}
